import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Switch,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const FieldConfigList = ({
  selectedModule,
  fieldConfigs,
  loading,
  hasChanges,
  onModuleChange,
  onFieldChange,
  onSave,
  onReset,
}) => {
  // 모듈 목록 정의
  const modules = [
    { code: 'CHANCE', label: '기회 관리' },
    { code: 'PROJECT', label: '프로젝트 관리' },
    { code: 'SALES', label: '영업 관리' },
    { code: 'TECH', label: '기술 관리' },
  ];

  // DataGrid 컬럼 정의
  const columns = [
    {
      field: 'fieldCd',
      headerName: '필드 코드',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'fieldNm',
      headerName: '필드명',
      width: 200,
      headerAlign: 'center',
    },
    {
      field: 'useYn',
      headerName: '사용 여부',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Switch
          checked={params.value === 'Y'}
          onChange={(e) =>
            onFieldChange(
              params.row.fieldConfigUuid,
              'useYn',
              e.target.checked ? 'Y' : 'N'
            )
          }
          color="primary"
        />
      ),
    },
    {
      field: 'requiredYn',
      headerName: '필수 여부',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Switch
          checked={params.value === 'Y'}
          onChange={(e) =>
            onFieldChange(
              params.row.fieldConfigUuid,
              'requiredYn',
              e.target.checked ? 'Y' : 'N'
            )
          }
          color="secondary"
          disabled={params.row.useYn === 'N'}
        />
      ),
    },
    {
      field: 'displayOrder',
      headerName: '표시 순서',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <TextField
          type="number"
          size="small"
          value={params.value || 0}
          onChange={(e) =>
            onFieldChange(
              params.row.fieldConfigUuid,
              'displayOrder',
              parseInt(e.target.value, 10)
            )
          }
          sx={{ width: 80 }}
        />
      ),
    },
    {
      field: 'status',
      headerName: '상태',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const isUsed = params.row.useYn === 'Y';
        const isRequired = params.row.requiredYn === 'Y';
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isUsed && (
              <Chip
                label="사용"
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            {!isUsed && (
              <Chip
                label="미사용"
                size="small"
                color="default"
                variant="outlined"
              />
            )}
            {isRequired && isUsed && (
              <Chip
                label="필수"
                size="small"
                color="error"
                variant="outlined"
              />
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      {/* 헤더 */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          필드 사용 설정
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasChanges && (
            <Chip 
              label="변경사항 있음" 
              color="warning" 
              size="small" 
            />
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onReset}
            disabled={loading || !hasChanges}
          >
            초기화
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={loading || !hasChanges}
            color="primary"
          >
            저장
          </Button>
        </Box>
      </Box>

      {/* 모듈 선택 탭 */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedModule}
          onChange={(e, newValue) => onModuleChange(newValue)}
          aria-label="모듈 선택"
        >
          {modules.map((module) => (
            <Tab
              key={module.code}
              label={module.label}
              value={module.code}
            />
          ))}
        </Tabs>
      </Box>

      {/* 안내 메시지 */}
      <Box sx={{ mb: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          • <strong>사용 여부</strong>: 해당 필드를 화면에 표시할지 설정합니다.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • <strong>필수 여부</strong>: 사용 중인 필드의 필수 입력 여부를 설정합니다.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • <strong>표시 순서</strong>: 화면에서 필드가 표시되는 순서를 설정합니다 (숫자가 작을수록 먼저 표시).
        </Typography>
      </Box>

      {/* 데이터 그리드 */}
      <Box sx={{ height: 600, width: '100%' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={fieldConfigs}
            columns={columns}
            getRowId={(row) => row.fieldConfigUuid}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            disableColumnMenu
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        )}
      </Box>

      {/* 푸터 정보 */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          총 {fieldConfigs.length}개 필드
        </Typography>
        {hasChanges && (
          <Typography variant="body2" color="warning.main">
            ⚠️ 변경사항을 저장하지 않으면 손실됩니다.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default FieldConfigList;
